import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api, clearSession, getStoredUser, getToken, setSession } from "../api/client";
import type { OtpRequestResponse, SessionUser, TokenResponse } from "../api/types";

type AuthState = {
  token: string;
  user: SessionUser | null;
  requestOtp: (identifier: string) => Promise<OtpRequestResponse>;
  verifyOtp: (identifier: string, code: string) => Promise<void>;
  loginPassword: (email: string, password: string) => Promise<void>;
  logout: () => void;
};

const AuthContext = createContext<AuthState | null>(null);

function applyToken(data: TokenResponse) {
  const user: SessionUser = {
    email: data.email,
    display_name: data.display_name,
    role: data.role,
  };
  setSession(data.access_token, user);
  return user;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState(getToken);
  const [user, setUser] = useState<SessionUser | null>(() => getStoredUser<SessionUser>());

  const requestOtp = useCallback(async (identifier: string) => {
    return api<OtpRequestResponse>("/api/auth/otp/request", {
      method: "POST",
      body: JSON.stringify({ email: identifier }),
    });
  }, []);

  const verifyOtp = useCallback(async (identifier: string, code: string) => {
    const data = await api<TokenResponse>("/api/auth/otp/verify", {
      method: "POST",
      body: JSON.stringify({ email: identifier, code }),
    });
    setUser(applyToken(data));
    setToken(data.access_token);
  }, []);

  const loginPassword = useCallback(async (email: string, password: string) => {
    const data = await api<TokenResponse>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    setUser(applyToken(data));
    setToken(data.access_token);
  }, []);

  const logout = useCallback(() => {
    clearSession();
    setToken("");
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ token, user, requestOtp, verifyOtp, loginPassword, logout }),
    [token, user, requestOtp, verifyOtp, loginPassword, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}
